//-- Copyright (c) 2021 TMT Insights Group, LLC
import { Theme } from '@mui/material';
import { AnimationSpec } from 'chart.js';
import React, { ReactElement, ReactNode } from 'react';

import {
  Availold,
  CommonConstants,
  CustomerProfile,
  DeepPartial,
  Exception,
  FocusAvail,
  FocusCalendar,
  FocusReport,
  FocusVersionScheduling,
  IngestFile,
  InventoryFileEntity,
  JobDetail,
  JobsStatsResponse,
  Order,
  OrderJob,
  OrderJobAsset,
  OrderJobTask,
  Pagination,
  TitleEntity,
  TitleResponse,
  TotalJobsCompletedByType,
  TransferQueueItem
} from '@/redux/slices/slice_types';
import { Roles } from '@/constants/constants';
import { ExtractedPaletteKeys } from '@/components/Dashboard/Widgets/Reusable/chartConfig';
import { FilterType } from '@/components/_common/Filters/FilterDictionary';
import { VideoPlayerProps } from '@/components/_common/VideoPlayer/VideoPlayer';
import { TitleEntityV2, TitleMetadata, TitleMetadataItem } from '@/redux/slices/titleV2Slice.types';
import { OPJobProfileDetailsFormCustomFieldsProps } from '@/components/OrderPlacement/OPJobAccordion/OPJobProfileDetailsForm';
import { getUiSchema } from '@/components/_common/SchemaForm/schemaFormUtils';
import { MediaInfoPreviewProps } from '@/components/IngestManagement/FileIngest/FileIngestMediaInfo';
import { ProfileDetailInfoCustomFieldsProps } from '@/components/OrderPlacement/OPStep1OrderDetails/ProfileDetails/ProfileDetailInfo';
import { LocalizationNamespace } from '@/i18n/types';

// *** CLIENT CONFIG TYPE ***
export interface ClientConfig {
  theme: ClientTheme;
  navLinks: NavLinks;
  viewingTitle: (title: TitleResponse) => React.JSX.Element;
  columns: AllTableColumnsConfig<unknown>;
  jobDetails: JobDetails;
  dataConfig: DataTableConfig;
  taskConfig: TaskConfig;
  titleConfig: TitleConfig;
  dashboardConfig: DashboardConfig;
  orderPlacementConfig: OrderPlacementConfig;
  ingestManagementConfig: IngestManagementConfig;
  notificationsConfig: NotificationsConfig;
  userColors: string[];
  versionColors: Record<string, string>;
  clientName: string;
  enableUserDocumentation: boolean;
  disableJobDetailProxy: boolean;
  notifications: boolean;
  jobTypeLabelColor: 'orange' | 'blue' | 'teal' | 'purple' | 'pink' | 'green' | 'lavender' | 'gray';
  taskTypeLabelColor: 'orange' | 'blue' | 'teal' | 'purple' | 'magenta' | 'green' | 'lavender' | 'gray';
  versionSelectShowUnspecifiedVersion?: boolean;
  commonConstants?: CommonConstants;
  bulkActionIcons?: {
    [key: string]: string;
  };
  componentOverrides?: ComponentOverrides;
  functionOverrides?: FunctionOverrides;
  filtersVersion?: number;
  defaultTimeZone?: string; // IANA timezone string like "America/New_York" for "EDT";
  titleHierarchyComponent?: TitleHierarchyComponent;
  omakasePlayerConfig?: OmakasePlayerClientConfig;
  bulkActionMaintainSelections?: string[];
  logoutConfig: LogoutConfig;
  userMenuOptions?: UserMenuOptions;
  bulkOrderEditVersion?: 1 | 2;
  fileBrowserConfig?: FileBrowserConfig;
  localization?: {
    namespace: LocalizationNamespace;
  };
}

export type UserMenuOptions = {
  emailDomainSuffix?: string;
  options: Array<{
    icon: string;
    label: string;
    url: string;
  }>;
};

export type LogoutConfig = {
  logoutEndpoint: string;
};

export type FunctionOverrides = {
  getUISchema?: typeof getUiSchema;
};

export type ComponentOverrides = {
  videoPlayer?: React.ComponentType<VideoPlayerProps>;
  profileDetailInfoCustomFields?: React.ComponentType<ProfileDetailInfoCustomFieldsProps>;
  orderPlacementJobProfileDetailsFormCustomFields?: React.ComponentType<OPJobProfileDetailsFormCustomFieldsProps>;
  fileIngestMediaInfo?: React.ComponentType<MediaInfoPreviewProps>;
};

type ClientTheme = {
  globalTheme: Theme;
  lightTheme: Theme;
  darkTheme: Theme;
};

// *** COLUMNS TYPES ***
export type AllTableColumnsConfig<T> = {
  [K in keyof T]?: ColumnConfig<T, K>;
} & {
  [key: string]: ColumnConfig<T, any>;
};

export interface ColumnConfig<T, K extends keyof T> {
  id?: string;
  label: any;
  minWidth?: number;
  maxWidth?: number;
  width?: number;
  flex?: number;
  sortField?: any;
  field?: any; // legacy
  disableSort?: boolean;
  defaultDisabled?: boolean;
  omitted?: boolean;
  clickCopy?: boolean;
  render?:
    | ((cell: T[K], row: T) => React.ReactNode)
    | ((props: { item: any; isCollapsed: boolean; hasCategories: boolean }) => React.ReactNode)
    | ((row: T, frozenRightWidth: number) => React.ReactNode)
    | ((cell: undefined, row: T, expanded: boolean, previewDimensions: { width?: number; height?: number }) => React.ReactNode);
  disableExpand?: (row: T) => boolean;
  isOverflow?: boolean;
  isExpandable?: boolean;
  isCheckbox?: boolean;
  required?: boolean;
  draggable?: <K extends keyof T>(cell: T[K], row: T) => boolean;
  tooltip?: {
    title: string;
    placement:
      | 'top'
      | 'left'
      | 'right'
      | 'bottom'
      | 'bottom-end'
      | 'bottom-start'
      | 'left-end'
      | 'left-start'
      | 'right-end'
      | 'right-start'
      | 'top-end'
      | 'top-start'
      | undefined;
  };
  position?: 'left' | 'right' | 'scrollable';
}

// *** DASHBOARD TYPES ***

// ts - replace status & filter with TaskStatus[] from constantSlice
export type StatusCard = {
  name: string;
  status: string;
  color: string;
  filter?: string[];
  valueKey?: string;
  ignoreOnPieChart?: boolean;
  icon: string;
  excludeStatus?: string[];
};

export type DefaultChartOverrides = {
  colors?: (string | ExtractedPaletteKeys)[];
};

export type DashboardConfig = {
  statusCards: StatusCard[];
  parseDataFn?: (list: JobsStatsResponse) => JobsStatsResponse;
  earliestDate?: string;
  graphList?: { id: string; checked: boolean }[];
  graphSettings: Partial<{
    default?: string[];
    'completed-jobs': DefaultChartOverrides & {
      displayed_job_types: string[];
      functional_displayed_job_types?: (totalJobsCompletedByType: TotalJobsCompletedByType) => void;
    };
    'on-time-delivery': DefaultChartOverrides & {
      datasets: {
        otd100?: 'On-Time Delivery';
        otd70?: 'On-Time Secondary';
      };
      colors?: (string | ExtractedPaletteKeys)[];
    };
    'media-delivery': DefaultChartOverrides & {
      datasets: {
        mediaDelivered: MediaDelivered;
        otd100: Otd100;
      };
    };
    'average-times': DefaultChartOverrides;
    'open-job-status': DefaultChartOverrides; // gets filters from status cards
    'outstanding-assets': DefaultChartOverrides;
    reports?: {
      asyncReports?: boolean;
    };
  }>;
  chartAnimation?: DeepPartial<AnimationSpec<any>>;
  chartColors?: (string | ExtractedPaletteKeys)[];
  maximumGraphsPerRow?: number;
  customizeDashboard?: boolean;
  maximumDailyReports?: number;
  customCycleDates?: Array<CustomCycleDate>;
  recentTasksConfig?: Partial<RecentTasksConfig>;
};

export interface RecentTasksConfig {
  filters: Partial<{
    asset_confirmation_filter: boolean;
  }>;
}

export interface CustomCycleDate {
  id: string;
  label: string;
  calculate: () => { startDate: string; endDate: string };
  queryParams?: (value?: any) => Record<string, string>;
}

export interface MediaDelivered {
  size: number;
  field: string;
}

export interface Otd100 {
  size: number;
  label: ReactElement;
}

export enum JobTypes {
  nothing = 'Nothing',
  delivery = 'Delivery',
  edit = 'Edit',
  tech_review = 'Tech Review',
  compliance = 'Compliance',
  ingest = 'Ingest',
  core_fix_edit = 'Core Fix Edit'
}

// *** DATACONFIG TYPES ***
export type DataTableConfig = {
  orders: Orders;
  jobs: Jobs;
  tasks: Tasks;
  pendingAssets: PendingAssets;
  processingTasks: ProcessingTasks;
  titles: Titles;
  titlesV2: Titles;
  fileIngest: IngestManager;
  transferQueue: TransferQueue;
  profiles: Profiles;
  jobDetailTasks: JobDetailTasks;
  jobDetailBundleJobs: Jobs;
  titleDetailJobs: Jobs;
  titleDetailAvails: Avails;
  exceptions: Exceptions;
  focusAvails: FocusAvails;
  focusCalendar: FocusCalendars;
  focusSchedule: FocusSchedules;
  focusReports: FocusReports;
  assemblyTemplates: AssemblyTemplates;
};

export type AssemblyTemplates = BaseTableConfiguration<{ data: any[] }, any[]> & {
  data: any[];
};

export type DataTableConfigValues =
  | Orders
  | Jobs
  | Tasks
  | PendingAssets
  | ProcessingTasks
  | Titles
  | IngestManager
  | TransferQueue
  | Profiles
  | JobDetailTasks
  | Exceptions;

export type BaseTableConfiguration<P, R> = {
  baseUrl: string;
  apiBaseUrl?: string;
  title: string;
  label: string;
  filters: FilterType[];
  params?: Params;
  settings?: Settings;
  pagination?: Pagination;
  preset?: Preset;
  multiSort?: boolean;
  parseDataFn?: (param: P) => R;
  disableGetData?: boolean;
  appliedSearchName?: any;
  expandedRowIds?: any;
  rowEditedId?: any;
  status?: any;
  enableGridViewToggle?: boolean;
  gridItemRender?: (row: IngestFile, key: string, onGridClick: (id: number) => void) => React.ReactNode;
  selected?: DataTableConfigValues['data'];
  filterOptions?: {
    [key: string]: string[];
  };
  tableType?: 'table' | 'calendar';
  bulkErrors?: string[][];
  quickFilters?: FilterType[];
  anyAllFilters?: FilterType[];
  // When true, delays the next table data fetch so rapid filter changes can be batched into a single request.
  debounceNextDataRequest?: boolean;
};

export interface FilterSettings {
  date: {
    due_date__gte: string;
    due_date__lte: string;
  };
  stage: (keyof Filters)[];
  [key: string]: any;
}

export type Settings = {
  filters: AppliedFilters;
  sorting: Sorting;
  currentPage: number;
  name?: string;
  disableGetData?: boolean;
  pageSize?: number;
};

export type SortingObject = {
  id: string;
  order: 'asc' | 'desc';
};

export type Sorting = SortingObject | string[];

export type Preset = {
  sorting?: Sorting;
  filters?: AppliedFilters;
};

export type Params = {
  name_filter?: string[]; // ts - all strings from the same list
  entity_types_filter?: string[]; // ts - replace with entity types list
  thumbnails?: boolean;
  status_filter?: string[];
  task_type_filter?: string[] | string;
};

export interface Filters {
  [key: string]: any[];
}

export type AppliedFilters = {
  [key in keyof Filters]: any;
};

export interface Orders extends BaseTableConfiguration<{ data: Order[] }, Order[]> {
  data: Order[];
}
export interface Jobs extends BaseTableConfiguration<Order, Order[]> {
  data: OrderJob[];
  selected?: OrderJob[];
}
export interface Avails extends BaseTableConfiguration<Availold, Availold[]> {
  data: Availold[];
}
export interface Tasks extends BaseTableConfiguration<OrderJobTask, OrderJobTask[]> {
  data: OrderJobTask[];
  orderOnPriorityAsArray?: boolean;
}
export interface PendingAssets extends BaseTableConfiguration<OrderJobAsset, OrderJobAsset[]> {
  data: OrderJobAsset[];
}
export interface ProcessingTasks extends BaseTableConfiguration<OrderJobTask, OrderJobTask[]> {
  data: OrderJobTask[];
}
export interface Titles extends BaseTableConfiguration<{ data: TitleEntity[] }, TitleEntity[]> {
  data: TitleEntity[];
  parseVersionsFn?: (list: TitleEntity[]) => TitleEntity[];
  enableTitleCustomerAssignment?: boolean;
}
export interface IngestManager extends BaseTableConfiguration<IngestFile, IngestFile[]> {
  data: IngestFile[];
  disableVideoPreview: boolean;
  gridItemRender?: (row: IngestFile, key: string, onGridClick: (id: number) => void) => React.ReactNode;
  titleAssociation?: {
    entity_types_filter: string[];
    prettyTitleDisplay?: boolean;
  };
}
export interface TransferQueue extends BaseTableConfiguration<{ response: { items: TransferQueueItem[] } }, TransferQueueItem[]> {
  data: TransferQueueItem[];
}
export interface Profiles extends BaseTableConfiguration<CustomerProfile, CustomerProfile[]> {
  data: CustomerProfile[];
  disableExport?: boolean;
}
export interface JobDetailTasks extends Partial<BaseTableConfiguration<OrderJobTask, OrderJobTask[]>> {
  data: OrderJobTask[];
  assetConfirmationFilterEnabled?: boolean;
}

export interface Exceptions extends BaseTableConfiguration<Exception, Exception[]> {
  data: Exception[];
}

export interface FocusAvails extends BaseTableConfiguration<FocusAvail, FocusAvail[]> {
  data: FocusAvail[];
}

export interface FocusSchedules extends BaseTableConfiguration<FocusVersionScheduling, FocusVersionScheduling[]> {
  data: FocusVersionScheduling[];
}

export interface FocusCalendars extends BaseTableConfiguration<FocusCalendar, FocusCalendar[]> {
  data: FocusCalendar[];
}

export interface FocusReports extends BaseTableConfiguration<FocusReport, FocusReport[]> {
  data: FocusReport[];
}

export type OrderDetailFields = {
  [key: string]: Field;
};

export type Field = {
  label: string;
  id: string;
};

// *** JOBDETAILS TYPES ***

export type JobDetails = {
  titleBar: () => TitleBar[];
  refreshCache?: boolean;
  ignoreBulkActions?: string[];
  showFullInputs?: boolean;
};

export type TitleBar = {
  key: string;
  label: string;
  render?: (data: JobDetail) => React.ReactNode;
};

// *** NAVLINKS ***

export type NavLinks = DirectLink[];

export type DirectLink = {
  label: string;
  id?: string;
  exact?: boolean;
  to: string;
  role?: Roles | string;
  component?: any;
  internal?: boolean;
  children?: DirectLink[];
};

export type ThemeColors = 'primary' | 'secondary' | 'tertiary' | 'success' | 'warning' | 'error' | 'background';
export enum TitleSearchMode {
  BULK_OR_PASTE = 'bulk_or_paste',
  DEFAULT = 'default'
}

// *** ORDERPLACEMENTCONFIG TYPES ***

export type OrderPlacementConfig = Partial<{
  entityTypes: string[];
  bulkTitleSearchOptions: Map<string, string>;
  titleSort: { order_on?: string; direction?: 'asc' | 'desc' };
  addlFields: AdditionalFields;
  viewingTitleHeader: (title: TitleEntity) => React.ReactNode;
  titleSearchResult: (title: TitleEntity) => React.ReactNode;
  outputHeader: (group: any, outputVersionObj: TitleEntity, profileEditType: string) => React.ReactNode;
  outputRevisedLabelColor: ThemeColors;
  versionDropdownOption: (version: TitleEntity, isTitleV2?: boolean) => React.ReactNode;
  versionOptionLabel: (version: TitleEntity) => React.ReactNode;
  findDefaultVersion: (versions: TitleEntity[], params: any) => TitleEntity | undefined;
  skipRallyIdCheck?: boolean;
  fileSelectionTitle: (version: TitleEntity | TitleEntityV2) => React.ReactNode;
  filterProfileList: (profiles: { [customerKey: string]: CustomerProfile[] }, customer: string, bussinessUnit?: string) => any;
  filterCustomerList: (currentCustomerList: string[], bussinessUnit?: string, profiles?: { [customerKey: string]: CustomerProfile[] }) => any;
  expandedVersionRows: (
    displayedVersions: (TitleEntity | TitleEntityV2)[],
    searchedVersion?: TitleEntity | TitleEntityV2,
    isTitleV2?: boolean,
    bussinessUnit?: string,
    params?: any
  ) => React.ReactNode;
  versionChipTooltip: (version: TitleEntity, tooltipSource?: string, hierarchy?: string) => React.ReactNode;
  componentized: Componentized;
  parseVersionsFn: (
    list: TitleEntity[],
    params?: {
      titleSearchValue?: string;
    }
  ) => TitleEntity[];
  parseFilesFn: (list: InventoryFileEntity[]) => InventoryFileEntity[];
  parseTitlesFn: (list: TitleEntity[]) => TitleEntity[];
  parseTitleMetadataFn: (metadata: any[], entityIds?: number[]) => TitleMetadata;
  renderMetadata: (metadata: TitleMetadataItem) => ReactNode;
  disableAddVersionFn: (entity: TitleEntity, businessUnit?: string, params?: any) => boolean;
  getLineageMetaTitle: (title: TitleEntity) => any;
  disableDueDate: boolean;
  disableEmbargoDate: boolean;
  disableSelectAllForVersions: boolean;
  displayTitleMetadataByHierarchy: boolean;
  bulkOrderJobDisplay: (job: OrderJob, title: string) => React.ReactNode;
  searchTermFilter: (versions: TitleEntity[], searchTerm: string) => TitleEntity | TitleEntity[] | undefined;
  selectedVersionTitleDisplay: (displayEpisodeNum: boolean, child: TitleEntity, version: TitleEntity, versionKey: string) => React.ReactNode;
  totalFilesSizeWarningThreshold: number; // Specifies the amount of bytes after which the warning should be displayed
  bulkOrderMetadata: (order: Order) => React.ReactNode;
  contextualSelectEntityIds: string[]; // Entity types that show parent view with selected item isolated (TitleBrowser)
  disableBulkEdit: boolean;
  checkEmbargoedVersions: boolean;
  shouldHideHiddenProfiles?: boolean;
  dueDateOffsetDays?: number;
  calculateInitialVersion?: (versions: TitleEntity[], search: string) => string | null | undefined;
  showFileTitleLabel?: boolean;
  disableCustomerCreateInProfileCreationFlow?: boolean;
  disableBillableCustomer?: boolean;
}>;

export type Componentized = {
  viewingTitle: (title: TitleEntity) => React.ReactNode;
  titleSearchResult?: (result: TitleEntity) => React.ReactNode;
};

export type AdditionalFields = {
  orderDetails: OrderDetails;
  profileDetails?: ProfileDetails;
};

export type OrderDetails = Partial<{
  po_number: Detail;
  billable_customer: Detail;
  budget_code: Detail;
  billing_code: Detail;
  cost_centre: Detail;
  opportunity_id: Detail;
  department_charge_code: Detail;
  line_of_business: Detail;
}>;

export type ProfileDetails = Partial<{
  assignee_user_id: Detail;
}>;

export type Detail = {
  label: string;
  id?: string;
  type?: 'autocomplete' | 'text' | 'radio';
  options?: string[];
  required?: boolean;
};

export type RallyTagProps = {
  [key: string]: {
    color: 'primary' | 'secondary' | 'default' | 'warning' | 'error' | 'info' | 'success';
    label: string;
    icon?: string;
  };
};

// *** TASKCONFIG ***

export type TaskConfig = {
  customFields?: {
    [key: string]: CustomField;
  };
};

export type CustomField = {
  label: string;
  cols: number;
  render?: (val: string) => any;
};

// *** TITLECONFIG ***

type FileStatus = 'available' | 'archive' | 'different' | 'future' | 'missing';
type TitleEntityParam = TitleEntity | TitleEntityV2;

export type TitleConfig = {
  metadata?: {
    [key: string]: Config;
  };
  enableMetadataUpdate?: boolean;
  shouldShowRelatedJobs?: boolean;
  parseDataFn?<T extends TitleEntityParam>(param: T): T;
  disableTMDB?: boolean;
  fileStatusFilter?: FileStatus[] | string[];
  customerLabel?: string;
  v2?: boolean;
  showAvails?: boolean;
};

export type IngestManagementConfig = {
  assetTypeValueMap: {
    [key: string]: string;
  };
  version?: 'v1' | 'v2';
  allowAutoIngestRegister?: boolean;
  disableRecentTitles?: boolean;
  readOnlyTitleAssociation?: boolean;
  fetchSignedUrl?: boolean;
  chart: {
    colors: string[];
    glow: {
      opacity: number;
      color: string;
    };
  };
  allowedUploadsBlurb?: string;
  registerAgainstWhiteList?: string[];
  versionRender?: (version: any) => string;
  disableReadyToRegisterForImages?: boolean;
  failedIngestUpdate?: boolean;
};

export type NotificationsConfig = {
  version?: 'v1' | 'v2';
};

export type Config = {
  label: string;
  entity_type?: string[];
  render?: (param: any, title: TitleEntity | TitleEntityV2, rootTitle: TitleEntity | TitleEntityV2) => React.ReactNode;
};

// utility for getting the type of an array by definition and NOT by value
export type ArrayElement<T> = T extends (infer U)[] ? U : never;

type TitleHierarchyTitleProp = Pick<TitleEntity, 'lineage_meta' | 'name' | 'entity_type' | 'og_entity_type' | 'sequence_order'> & {
  children: TitleHierarchyTitleProp[];
};

export type TitleHierarchyComponent = React.FC<{
  title: TitleHierarchyTitleProp;
  nameFallback?: string;
  versionName?: string;
}>;

export enum OmakasePlayerFeature {
  CUSTOM_MARKERS = 'custom_markers',
  POINTS_OF_INTEREST = 'points_of_interest',
  BLACK_SEGMENTS = 'black_segments',
  SUBTITLES = 'subtitles',
  AUDIO_TRACKS = 'audio_tracks',
  THUMBNAILS = 'thumbnails'
}

export type OmakasePlayerClientConfig = {
  features?: OmakasePlayerFeature[];
  enableContentPlayback?: boolean;
};

export type FileBrowserConfig = {
  renderFileName?: (fileName: string) => ReactElement;
};
